import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";
import ReactMarkdown from "react-markdown";
import rehypeSanitize from "rehype-sanitize";
// Optional: import remarkGfm from "remark-gfm";

type FAQ = {
  id: string;
  slug: string;
  title: string;
  body_md: string;
  tags: string[] | null;
  created_at: string;
  updated_at: string;
};

export default function FAQEntry() {
  const { slug } = useParams();
  const [faq, setFaq] = useState<FAQ | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    if (!slug) return;
    setLoading(true);
    setError(null);

    const { data, error } = await supabase.rpc("faq_by_slug", { p_slug: slug });
    if (error) {
      console.error("faq_by_slug failed:", error.message);
      setError("Eintrag konnte nicht geladen werden.");
      setFaq(null);
      setLoading(false);
      return;
    }

    const row = Array.isArray(data) ? data[0] : data;
    if (!row) {
      setError("Eintrag nicht gefunden.");
      setFaq(null);
      setLoading(false);
      return;
    }

    setFaq(row as FAQ);
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  if (loading)
    return (
      <div className="min-h-screen bg-[#0d1117] flex items-center justify-center text-slate-500">
        ⏳ Lade Eintrag…
      </div>
    );

  if (error || !faq)
    return (
      <div className="min-h-screen bg-[#0d1117]">
        <div className="max-w-3xl mx-auto pt-20 pb-20 px-4 sm:px-6">
          <p className="text-red-400">{error ?? "Unbekannter Fehler."}</p>
          <div className="mt-4">
            <Link to="/faq" className="text-sky-400 hover:underline text-sm">
              ← Zur Übersicht
            </Link>
          </div>
        </div>
      </div>
    );

  return (
    <div className="min-h-screen bg-[#0d1117]">
      <div className="max-w-3xl mx-auto pt-20 pb-20 px-4 sm:px-6">
        <div className="mb-2">
          <Link to="/faq" className="text-sky-400 hover:underline text-sm">
            ← Zur Übersicht
          </Link>
        </div>

        <h1 className="text-2xl font-bold text-slate-100">{faq.title}</h1>

        {(faq.tags ?? []).length > 0 && (
          <div className="flex flex-wrap gap-2 mt-2">
            {(faq.tags ?? []).map((t) => (
              <Link
                key={t}
                to={`/faq?tag=${encodeURIComponent(t)}`}
                className="px-2 py-0.5 rounded-full text-xs border border-[#2a3347] bg-[#1e2639] text-slate-400 hover:bg-[#2a3347]"
              >
                #{t}
              </Link>
            ))}
          </div>
        )}

        <div className="prose prose-invert mt-4 max-w-none">
          <ReactMarkdown rehypePlugins={[rehypeSanitize]}>
            {faq.body_md}
          </ReactMarkdown>
        </div>

        <div className="mt-6 text-xs text-slate-500">
          Aktualisiert am {new Date(faq.updated_at).toLocaleString()}
        </div>
      </div>
    </div>
  );
}
